import { useState, useEffect, useRef, FormEvent } from "react";
import { createFilterOptions, updateActiveTask, Util } from '../../util/Util'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../components/RefreshButton";
import { SelectInstance } from "react-select";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { useAuth } from "../../hooks/useAuth";
import { focusAndSelect } from "../../util/focusField";
import { MYSQL_LIMITS } from "../../util/constants";
import { Button, Checkbox, Grid, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Add, AddBoxOutlined, RemoveCircleOutline, Sync } from "@mui/icons-material";
import { renderCatalogItemOption } from "../../components/itemAutocompleteRenderOption";
import {
	catalogItemAutocompleteInteractionProps,
	handleCatalogItemAutocompleteChange,
	isCatalogItemOptionEqualToValue,
} from "../../components/catalogItemAutocomplete";
import { applySavedCatalogOption, CatalogItemPickerProvider } from "../../components/CatalogItemActions";
import type { CatalogItemRow } from "./ItemDetailModal";
import { Item, useInventory } from "./InventoryContext";
import { flushItemTableItems } from "../../util/itemTableFlush";
import { refreshInventoryLines } from "./refreshLines";
import { toast } from "react-toastify";
import VirtualizedListbox from "../../components/VirtualizedListbox";

interface Props {
	bundle?: any;
}

interface OptionType {
	value: number;
	label: string;
}

const InventoryItemDetail = ({ bundle }: Props) => {
	const [barcodes, setBarcodes] = useState<any>({});
	const [selectedItem, setSelectedItem] = useState<Item | null>(null);
	const [errorInput, setErrorInput] = useState("");
	const [isItemLoading, setIsItemLoading] = useState(false);
	const [isUpdatingLines, setIsUpdatingLines] = useState(false);
	const [useBarcode, setUseBarcode] = useState(false);
	const [barcode, setBarcode] = useState("");

	const { pendingTasks, setPendingTasks, activeTask, items, setItems, apiRef } = useInventory();
	const { queueReRun, t } = useUI();
	const { appSettings } = useAppSocket();
	const { api } = useAuth();

	const barcodeRef = useRef<HTMLInputElement>(null);
	const itemRef = useRef<SelectInstance<OptionType, false>>(null);
	// Counts are typed in the grid: after picking an item, Enter on Add puts the line there.
	const addBtnRef = useRef<HTMLButtonElement>(null);
	const prevSourceRef = useRef<number | null>(null);
	const prevTaskIdRef = useRef<string | null>(null);

	const filteredItems = items.filter(
		(item) => !activeTask?.items?.some((taskItem) => taskItem.id === item.id)
	);

	/** What the system believes is in the depot — the only thing "Add all" seeds. */
	const inDepotItems = items.filter((item: any) => item.InDepot !== false);

	const pendingInDepotCount = inDepotItems.filter(
		(item: any) => !activeTask?.items?.some((taskItem) => taskItem.id === item.id)
	).length;

	const toCountLine = (item: any) =>
		new Item({
			id: item.id,
			item_code: item.ItemCode,
			item: item.ItemName,
			price: item.CostPrice,
			stock: item.Stock,
			quantity: item.Stock < 0 ? 0 : item.Stock,
			sector: item.Sector,
			category: item.Category,
			isFractional: item.IsFractional === true,
		});

	const fetchItems = async () => {
		if ((activeTask?.depot || -1) < 1) return;

		setIsItemLoading(true);

		// IncludeCatalog: a count can turn up an item the system does not have
		// in this depot at all. Those come back flagged InDepot=false.
		const res = await api.get(`depots/${activeTask?.depot}/items`, {
			params: {
				IncludeExtra: true,
				IncludeCatalog: true,
			}
		});

		let fresh: any[] | null = null;

		if (res) {
			if (res.status === 200) {
				fresh = res.data.items;
				setItems(res.data.items);
				setBarcodes(res.data.barcodes);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchItems);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsItemLoading(false);

		return fresh;
	};

	/**
	 * Refresh the lines already counted: name, code, unit value, system stock,
	 * sector, category and the fractional flag come from the depot again.
	 * The item and its count are what the counter entered, so they stay.
	 */
	const onUpdateLinesClicked = async () => {
		if (!activeTask || isUpdatingLines) return;

		setIsUpdatingLines(true);
		const fresh = await fetchItems();

		if (fresh) {
			const current = flushItemTableItems<Item>(apiRef) ?? activeTask.items ?? [];
			const { lines, updated, missing } = refreshInventoryLines(current, fresh);

			updateActiveTask(setPendingTasks, { items: lines });
			toast.success(t("common:inventoryModal.refreshLines.done", { updated, missing }), { autoClose: 3000 });
		}

		setIsUpdatingLines(false);
	};

	const handleCatalogSaved = (updated: CatalogItemRow) => {
		setItems((prev) => applySavedCatalogOption(prev, updated));
		setSelectedItem((prev) =>
			prev && prev.id === updated.id
				? new Item({
					...prev,
					item: updated.ItemName || prev.item,
					item_code: updated.ItemCode || prev.item_code,
					isFractional: updated.IsFractional === true,
				})
				: prev
		);
		updateActiveTask(setPendingTasks, (prev) => ({
			...prev,
			items: prev.items?.map((it) =>
				it.id === updated.id
					? new Item({
						...it,
						item: updated.ItemName || it.item,
						item_code: updated.ItemCode || it.item_code,
						isFractional: updated.IsFractional === true,
					})
					: it
			),
		}));
	};

	const onItemChanged = (item_id: number) => {
		const item: any = filteredItems.find((item: any) => item.id === item_id);

		if (item) {
			setSelectedItem(toCountLine(item));
			requestAnimationFrame(() => addBtnRef.current?.focus());
		} else {
			setSelectedItem(null);
		}
	};

	const lookupBarcode = (code: string = barcode) => {
		const itemID = barcodes[code];

		if (itemID) {
			onItemChanged(itemID);
		} else {
			itemRef.current?.focus();
			setErrorInput("barcodeInput");
			setTimeout(() => setErrorInput(""), 2000);
		}
	}

	const onAddClicked = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (selectedItem && activeTask) {

			updateActiveTask(setPendingTasks, (prev) => {
				return { ...prev, items: [...prev.items!, selectedItem] };
			});
		}

		focusAndSelect(useBarcode ? barcodeRef : itemRef);
	};

	useEffect(() => {
		if (activeTask) {
			const currentTaskId = activeTask.id;
			if (prevTaskIdRef.current) {
				if (prevTaskIdRef.current !== currentTaskId) {
					fetchItems();
				} else {
					const currentSource = activeTask.depot;
					if (prevSourceRef.current !== currentSource) {
						fetchItems();
					}
					prevSourceRef.current = currentSource;
				}
				prevTaskIdRef.current = currentTaskId;
			} else {
				fetchItems();
				prevTaskIdRef.current = currentTaskId;
			}
		}
	}, [pendingTasks]);

	useEffect(() => {
		focusAndSelect(useBarcode ? barcodeRef : itemRef);
	}, [useBarcode]);

	useEffect(() => {
		setUseBarcode(Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true));
	}, []);

	return (
		<form onSubmit={onAddClicked} style={{ marginBottom: '10px' }}>
			<Grid container rowSpacing={0} columnSpacing={0}>
				<Grid size={{ xs: 12, sm: 6, md: 2 }} >
					<TextField
						id="barcodeInput"
						size="small"
						label={
							<span >
								{t("common:ui.labels.barcode")}&nbsp;&nbsp;
								<Checkbox
									checked={useBarcode}
									onChange={() => setUseBarcode(!useBarcode)}
								/>
							</span>
						}
						variant="filled"
						fullWidth
						type="text"
						value={barcode}
						inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
						inputRef={barcodeRef}
						slotProps={{
							input: {
								endAdornment: <FontAwesomeIcon icon={faMagnifyingGlass} onClick={() => lookupBarcode()} />,
							}
						}}
						onFocus={(e) => e.target.select()}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								lookupBarcode(barcode);
							}
						}}
						onChange={(e) => setBarcode(e.target.value)}
						disabled={!activeTask || isItemLoading || Object.keys(barcodes).length === 0}
						error={errorInput === "barcodeInput"}
						helperText={
							errorInput === "barcodeInput"
								? t("common:stockTrace.barcodeNotFound")
								: ""
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 3 }} >
					<CatalogItemPickerProvider onItemSaved={handleCatalogSaved}>
					<Autocomplete
						id="itemInput"
						size="small"
						options={filteredItems}
						ListboxComponent={VirtualizedListbox}
						filterOptions={createFilterOptions((opt) => opt.ItemName || "")}
						{...catalogItemAutocompleteInteractionProps}
						getOptionLabel={(item) => item.ItemName || ""}
						isOptionEqualToValue={isCatalogItemOptionEqualToValue}
						renderOption={(props, option) => renderCatalogItemOption(props, option, "both")}
						value={filteredItems.find((i) => i.id === selectedItem?.id) || null}
						onChange={(_, newValue, reason) => {
							handleCatalogItemAutocompleteChange(reason, newValue, {
								onSelect: (id) => onItemChanged(id),
								onClear: () => onItemChanged(-1),
							});
						}}
						loading={isItemLoading}
						renderInput={(params) => (
							<TextField
								{...params}
								label={
									<span>
										{t("common:ui.labels.item")}&nbsp;&nbsp;
										<RefreshButton isWorking={isItemLoading} onClick={fetchItems} disabled={isItemLoading} />
									</span>
								}
								placeholder={isItemLoading ? t("common:ui.placeholders.loadingItems") : t("common:ui.placeholders.selectItem")}
								required
								onFocus={(e) => e.target.select()}
								inputRef={itemRef}
								variant="filled"
								fullWidth
							/>
						)}
						sx={{
							'& .MuiAutocomplete-listbox': {
								padding: 0,             // remove extra padding around list
							},
							'& .MuiAutocomplete-option': {
								padding: '1px 1px',     // make each option compact
								fontSize: '5rem',   // smaller font
								minHeight: 'auto',      // remove default minHeight
							},
							'& .MuiAutocomplete-popupIndicator': {
								padding: '4px',         // smaller icon padding
							},
						}}
						ListboxProps={{
							style: {
								maxHeight: 400,
							},
						}}
					/>
					</CatalogItemPickerProvider>
				</Grid>
				{filteredItems.length > 0 &&
					<Grid size={{ xs: 6, sm: 6, md: 1 }} display="flex" justifyContent="center" alignItems="start">
						<Button ref={addBtnRef} fullWidth size="large" type="submit" color="success" startIcon={<Add />} sx={{ fontWeight: 'bolder' }}>
							{t("common:ui.buttons.add")}
						</Button>
					</Grid>
				}
				{(activeTask?.items?.length || 0) > 0 &&
					<Grid size={{ xs: 6, sm: 6, md: 1 }} display="flex" justifyContent="center" alignItems="start">
						<Button
							onClick={(e) => {
								e.preventDefault();
								onUpdateLinesClicked();
							}}
							disabled={isItemLoading || isUpdatingLines}
							title={t("common:inventoryModal.refreshLines.hint")}
							fullWidth size="large" type="button" color="info" startIcon={<Sync />} sx={{ fontWeight: 'bolder' }}>
							{t("common:inventoryModal.refreshLines.button")}
						</Button>
					</Grid>
				}
				{pendingInDepotCount > 0 &&
					<Grid size={{ xs: 6, sm: 6, md: 1 }} display="flex" justifyContent="center" alignItems="start">
						<Button
							onClick={(e) => {
								e.preventDefault();

								// Merge, never replace: off-depot items found during the
								// count are added one by one and must survive this.
								updateActiveTask(setPendingTasks, (prev) => {
									const existing = prev.items || [];
									const existingIds = new Set(existing.map((it) => Number(it.id)));
									const additions = inDepotItems
										.filter((item: any) => !existingIds.has(Number(item.id)))
										.map(toCountLine);

									return { ...prev, items: [...existing, ...additions] };
								});
							}}
							fullWidth size="large" type="button" color="success" startIcon={<AddBoxOutlined />} sx={{ fontWeight: 'bolder' }}>
							{t("common:ui.buttons.addAll")}
						</Button>
					</Grid>
				}
				{apiRef.current && apiRef.current.getSelectedRows().size > 0 &&
					<Grid size={{ xs: 6, sm: 6, md: 1 }} display="flex" justifyContent="center" alignItems="start">
						<Button
							onClick={(e) => {
								e.preventDefault();

								const selectedIds = Array.from(apiRef.current!.getSelectedRows().keys());

								updateActiveTask(setPendingTasks, (prev) => ({
									...prev,
									items: prev.items
										? prev.items.filter((item: Item) => !selectedIds.includes(item.id.toString()))
										: [],
								}));
							}}
							fullWidth size="large" type="button" color="error" startIcon={<RemoveCircleOutline />} sx={{ fontWeight: 'bolder' }}>
							{t("common:ui.buttons.remove")}
						</Button>
					</Grid>
				}
				{apiRef.current && apiRef.current.getRowsCount() > 0 &&
					<Grid size={{ xs: 6, sm: 6, md: 2 }} display="flex" justifyContent="center" alignItems="start">
						<Button
							onClick={(e) => {
								e.preventDefault();
								updateActiveTask(setPendingTasks, { items: [] });
							}}
							fullWidth size="large" type="button" color="error" startIcon={<RemoveCircleOutline />} sx={{ fontWeight: 'bolder' }}>
							{t("common:ui.buttons.clearAll")}
						</Button>
					</Grid>
				}
			</Grid>
		</form>
	);
};

export default InventoryItemDetail;
